// The entity editor's per-field settings: the flags, the settings only some types have, and the toggle showing them.
import { Fragment } from "preact";
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemCounts, ObjEntity, SpaceOut, TextFormat } from "../../api/types.gen.ts";
import { locale, t } from "../../i18n/index.ts";
import { type Row, UNIQUE_TYPES } from "../../lib/fieldRows.ts";
import { CURRENCIES } from "../../lib/format.ts";
import {
  ANY_REGION,
  BR_STATES,
  FORMAT_GROUPS,
  kindOf,
  PLATE_REGIONS,
  phoneRegions,
  regionName,
  UNITS,
  unitSymbol,
} from "../../lib/formats.ts";
import { ignore } from "../../lib/ignore.ts";
import { followJob } from "../../lib/jobs.ts";
import { spaceApi } from "../../lib/paths.ts";
import { Custom } from "../../lib/pattern.ts";
import { useAction } from "../../lib/resource.ts";
import { toast } from "../../lib/toast.ts";
import { useId } from "../../lib/uid.ts";
import { zoneOptions } from "../../lib/zone.ts";
import { Button, IconButton } from "../../ui/Button.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { Disclosure } from "../../ui/Disclosure.tsx";
import { Input, Select } from "../../ui/form.tsx";
import { AddToListIcon, SlidersIcon } from "../../ui/icons.tsx";
import { Segmented } from "../../ui/Segmented.tsx";
import { TokenInput } from "../../ui/TokenInput.tsx";
import { NormalizeDialog } from "../items/normalize.tsx";

const CHECKBOX = "size-3.5 accent-blue-600";
/** A setting's label, on the line of its (compact) control. */
const SETTING_LABEL = "shrink-0 text-xs leading-7 text-zinc-500";
/** The settings toggle (and the gap standing in for it): one width, so all type selects end alike. */
const SQUARE = { normal: "size-9", compact: "size-7" };
/** The most options "Use values in items" makes: more different values than this are free text, not choices. */
const MOST_OPTIONS = 100;

interface SettingProps {
  r: Row;
  env: Env;
  /** A ref field's link field (its values are on links, not in items' data). */
  link?: boolean;
  set: (p: Partial<Row>) => void;
}

/**
 * Adds to the options the values the entity's items have in the field as saved (text, or choices outside the
 * options), the most common first; refuses when there are too many to be choices.
 */
function FromItems({ r, env, set }: SettingProps) {
  const { space, saved } = env;
  const [fill, busy] = useAction(async () => {
    if (!(space && saved && r.orig)) {
      return;
    }
    const by = `data.${r.orig}`;
    const c = await api.get<ItemCounts>(spaceApi(space, "/items/counts"), {
      entity: saved.id,
      by,
      limit: MOST_OPTIONS,
    });
    const options = r.options ?? [];
    const found = c.values.map((v) => v.value).filter((v) => !options.includes(v));
    if (c.more) {
      toast(t("fields.tooManyValues", { count: MOST_OPTIONS }), "error");
    } else if (found.length === 0) {
      toast(t("fields.noNewValues"));
    } else {
      set({ options: [...options, ...found] });
      toast(t("fields.valuesAdded", { count: found.length }));
    }
  });
  return (
    <Button size="xs" busy={busy} title={t("fields.fromItemsHint")} onClick={fill} class="shrink-0">
      <AddToListIcon size={14} />
      {t("fields.fromItems")}
    </Button>
  );
}

/** Display names for the options (optional: an option without one shows as itself), each beside its option. */
function OptionLabels({ r, set }: SettingProps) {
  const labels = r.labels ?? {};
  const options = r.options ?? [];
  const named = options.filter((o) => labels[o]?.trim()).length;
  return (
    <Disclosure
      open={named > 0 && options.length <= 5}
      summary={named > 0 ? t("fields.optionLabelsCount", { count: named }) : t("fields.optionLabels")}
    >
      <div class="grid grid-cols-[auto_minmax(0,18rem)] items-center justify-start gap-x-3 gap-y-1 pt-1 pb-1">
        {options.map((o) => (
          <Fragment key={o}>
            <span class="inline-flex h-5 items-center justify-self-start rounded bg-zinc-100 px-1.5 text-xs text-zinc-800">
              {o}
            </span>
            <Input
              compact={true}
              aria-label={t("fields.optionLabel", { option: o })}
              placeholder={o}
              value={labels[o] ?? ""}
              onValue={(v) => set({ labels: { ...labels, [o]: v } })}
            />
          </Fragment>
        ))}
      </div>
    </Disclosure>
  );
}

/** A select field's options, as chips, their display names, and (on a saved field) filling them from items. */
function OptionsSetting(p: SettingProps) {
  const { r, env, set } = p;
  const id = useId();
  const fillable =
    !(p.link || env.readOnly) &&
    env.saved?.fields?.some((f) => f.key === r.orig && (f.type === "text" || f.type === "select"));
  return (
    // The options' label in one column; the chips, then their display names, lined up in the other.
    <div class="grid w-full max-w-3xl grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2">
      <label for={id} class={SETTING_LABEL}>
        {t("fields.options")}
      </label>
      <div class="flex items-start gap-2">
        <TokenInput
          id={id}
          values={r.options ?? []}
          onValues={(options) => set({ options })}
          removeLabel={(option) => t("fields.removeOption", { option })}
          placeholder={t("fields.optionsHint")}
          title={t("fields.optionsHint")}
        />
        {fillable ? <FromItems {...p} /> : null}
      </div>
      {r.options?.length ? (
        <div class="col-start-2">
          <OptionLabels {...p} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * A ref field as a sentence: what its links are (just links, the item's parts, or the whole it's part of), to what (any
 * item, or one entity's), and whether each target is linked once. Each choice's title says it with the real names.
 */
function RefSettings({ r, env, set }: SettingProps) {
  // "Items in “Documents” are parts of Operation", "Document is part of the item in “Operation”".
  const names = { entity: env.saved?.name || t("fields.thisEntity"), field: r.label || r.key || t("fields.thisField") };
  return (
    <>
      <div class="flex items-center gap-2">
        <Segmented
          label={t("fields.ownership")}
          size="xs"
          value={r.ownership ?? "none"}
          onValue={(v) => set({ ownership: v === "none" ? null : v })}
          options={[
            {
              value: "none",
              label: t("fields.linksTo"),
              title: t("fields.ownershipNone"),
            },
            {
              value: "contains",
              label: t("fields.contains"),
              title: t("fields.ownershipContains", names),
            },
            {
              value: "part_of",
              label: t("fields.partOf"),
              title: t("fields.ownershipPartOf", names),
            },
          ]}
        />
        <Select
          aria-label={t("fields.target")}
          compact={true}
          value={r.target ?? ""}
          onValue={(v) => set({ target: v || null })}
          options={[
            { value: "", label: t("fields.anyItem") },
            ...env.entities.map((e) => ({ value: e.id, label: e.name })),
          ]}
        />
      </div>
      <div class="flex h-7 items-center">
        <Flag
          text={t("fields.uniqueShort")}
          title={t("fields.unique")}
          label={t("fields.unique")}
          checked={r.unique ?? false}
          onCheck={(v) => set({ unique: v })}
        />
      </div>
    </>
  );
}

/** A money field's currency: a default for new values, or (fixed) the only one allowed. */
function MoneySettings({ r, set }: SettingProps) {
  const id = useId();
  return (
    <>
      <div class="flex items-center gap-2">
        <label for={id} class={SETTING_LABEL}>
          {t("fields.currency")}
        </label>
        <Select
          id={id}
          title={t(r.fixed_currency ? "fields.currencyFixed" : "fields.currencyDefault")}
          compact={true}
          value={r.currency ?? ""}
          onValue={(v) => set({ currency: v || null, ...(v ? {} : { fixed_currency: false }) })}
          options={[{ value: "", label: t("fields.noDefault") }, ...CURRENCIES.map((c) => ({ value: c, label: c }))]}
        />
      </div>
      <div class="flex h-7 items-center">
        <Flag
          text={t("fields.fixedShort")}
          title={t("fields.fixedHint")}
          label={t("fields.fixedHint")}
          checked={r.fixed_currency ?? false}
          disabled={!r.currency}
          onCheck={(v) => set({ fixed_currency: v })}
        />
      </div>
    </>
  );
}

/** A zoned date-time field's default zone for new values (unset = the space's). */
function ZoneSettings({ r, env, set }: SettingProps) {
  const id = useId();
  return (
    <div class="flex items-center gap-2">
      <label for={id} class={SETTING_LABEL}>
        {t("fields.defaultZone")}
      </label>
      <Select
        id={id}
        compact={true}
        value={r.time_zone ?? ""}
        onValue={(v) => set({ time_zone: v || null })}
        options={[{ value: "", label: t("fields.orgZone", { zone: env.orgZone }) }, ...zoneOptions()]}
      />
    </div>
  );
}

/** The region select: the space's by default (`none`: what's said when the space has none), else one of `regions`. */
function RegionSetting({ r, env, set, regions, any }: SettingProps & { regions: readonly string[]; any?: boolean }) {
  const id = useId();
  const lang = locale.value;
  const space = env.space?.region;
  const named = regions
    .map((c) => ({ value: c, label: regionName(c, lang) }))
    .sort((a, b) => a.label.localeCompare(b.label, lang));
  return (
    <div class="flex items-center gap-2">
      <label for={id} class={SETTING_LABEL}>
        {t("fields.region")}
      </label>
      <Select
        id={id}
        compact={true}
        value={r.region ?? ""}
        onValue={(v) => set({ region: v || null })}
        options={[
          {
            value: "",
            label: space
              ? t("fields.spaceRegion", { region: regionName(space, lang) })
              : t(any ? "fields.spaceNoRegionAny" : "fields.spaceNoRegion"),
          },
          ...(any ? [{ value: ANY_REGION, label: t("fields.anyRegion") }] : []),
          ...named,
        ]}
      />
    </div>
  );
}

/** Unique (data fields): no two items of the entity with the same value, compared as the format reads them. */
function UniqueSetting({ r, link, set }: SettingProps) {
  if (link || !UNIQUE_TYPES.has(r.type)) {
    return null;
  }
  return (
    <div class="flex h-7 items-center">
      <Flag
        text={t("fields.uniqueShort")}
        title={t("fields.uniqueValues")}
        label={t("fields.uniqueValues")}
        checked={r.unique ?? false}
        onCheck={(v) => set({ unique: v })}
      />
    </div>
  );
}

/** An IE field's state: its rules only (none: any state's). */
function StateSetting({ r, set }: SettingProps) {
  const id = useId();
  return (
    <div class="flex items-center gap-2">
      <label for={id} class={SETTING_LABEL}>
        {t("fields.state")}
      </label>
      <Select
        id={id}
        compact={true}
        value={r.region ?? ""}
        onValue={(v) => set({ region: v || null })}
        options={[
          { value: "", label: t("fields.anyState") },
          ...Object.entries(BR_STATES).map(([code, name]) => ({ value: `BR-${code}`, label: `${name} (${code})` })),
        ]}
      />
    </div>
  );
}

/** The region a format's values read in: a plate's country, a VIN's (its check digit in the US and Canada), an IE's state. */
function FormatRegion(p: SettingProps) {
  switch (p.r.format) {
    case "license_plate":
      return <RegionSetting {...p} regions={PLATE_REGIONS} any={true} />;
    case "vin":
      return <RegionSetting {...p} regions={phoneRegions()} />;
    case "br_ie":
      return <StateSetting {...p} />;
    default:
      return null;
  }
}

/** A text field's own format, without one of the list's: a pattern its values match and a mask they're typed in. */
function PatternSettings({ r, set }: SettingProps) {
  const [pid, mid] = [useId(), useId()];
  const spec = r.pattern || r.mask ? Custom.of(r.pattern || null, r.mask || null) : null;
  const refused = spec && !(spec instanceof Custom) ? spec.error : null;
  return (
    <>
      <div class="flex items-center gap-2">
        <label for={pid} class={SETTING_LABEL}>
          {t("fields.pattern")}
        </label>
        <Input
          id={pid}
          compact={true}
          class="w-56 font-mono"
          spellcheck={false}
          autocomplete="off"
          placeholder="[A-Z]{2}[0-9]{4}"
          title={t("fields.patternHint")}
          value={r.pattern ?? ""}
          aria-invalid={refused === "pattern" || undefined}
          onValue={(v) => set({ pattern: v || null })}
        />
      </div>
      <div class="flex items-center gap-2">
        <label for={mid} class={SETTING_LABEL}>
          {t("fields.mask")}
        </label>
        <Input
          id={mid}
          compact={true}
          class="w-40 font-mono"
          spellcheck={false}
          autocomplete="off"
          placeholder="000.000-00"
          title={t("fields.maskHint")}
          value={r.mask ?? ""}
          aria-invalid={refused === "mask" || undefined}
          onValue={(v) => set({ mask: v || null })}
        />
      </div>
      {refused ? (
        <p class="text-xs text-amber-700">
          {t(refused === "pattern" ? "fields.patternInvalid" : "fields.maskInvalid")}
        </p>
      ) : null}
    </>
  );
}

/**
 * On a saved field whose values read as a format or type: Normalize, rewriting the entity's items' values of it in
 * their standard form (what it would do is counted first).
 */
function NormalizeSetting({ r, env, link }: SettingProps) {
  const [open, setOpen] = useState(false);
  const { space, saved: entity } = env;
  const saved = entity?.fields?.find((f) => f.key === r.orig);
  const reads = saved && (saved.type === "location" || kindOf(saved.type, saved.format, saved) !== undefined);
  if (link || env.readOnly || !space || !entity || !saved || !reads) {
    return null;
  }
  return (
    <>
      <Button size="xs" title={t("normalize.what")} onClick={() => setOpen(true)} class="shrink-0">
        {t("normalize.button")}
      </Button>
      <NormalizeDialog
        space={space}
        open={open}
        onClose={() => setOpen(false)}
        title={t("normalize.titleField", { field: saved.label || saved.key })}
        target={{ filter: { entity: entity.id } }}
        fields={[saved.key]}
        onDone={(done) => {
          if ("action" in done) {
            followJob(space, done).catch(ignore);
          } else if (done.items > 0) {
            toast(t("jobs.done.normalize", { count: done.items }));
          }
        }}
      />
    </>
  );
}

/** A text field's format (none: free text, or its own pattern and mask), and the region some formats read in. */
function FormatSettings(p: SettingProps) {
  const { r, set } = p;
  const id = useId();
  return (
    <>
      <div class="flex items-center gap-2">
        <label for={id} class={SETTING_LABEL}>
          {t("fields.format")}
        </label>
        <Select
          id={id}
          compact={true}
          value={r.format ?? ""}
          onValue={(v) => set({ format: (v || null) as TextFormat | null, region: null })}
          options={[
            { value: "", label: t("fields.noFormat") },
            ...FORMAT_GROUPS.flatMap(({ group, formats }) =>
              formats.map((f) => ({ value: f, label: t(`format.${f}`), group: t(`formatGroup.${group}`) })),
            ),
          ]}
        />
      </div>
      <FormatRegion {...p} />
      {r.format ? null : <PatternSettings {...p} />}
      <UniqueSetting {...p} />
      <NormalizeSetting {...p} />
    </>
  );
}

/** A phone field's region (national numbers are read in it) and whether only international numbers are valid. */
function PhoneSettings(p: SettingProps) {
  return (
    <>
      <RegionSetting {...p} regions={phoneRegions()} />
      <div class="flex h-7 items-center">
        <Flag
          text={t("fields.strictShort")}
          title={t("fields.strictHint")}
          label={t("fields.strictHint")}
          checked={p.r.strict ?? false}
          onCheck={(v) => p.set({ strict: v })}
        />
      </div>
      <UniqueSetting {...p} />
      <NormalizeSetting {...p} />
    </>
  );
}

/** The units, grouped by dimension, as options. */
const unitOptions = () => UNITS.map(([u, d]) => ({ value: u, label: unitSymbol(u), group: t(`dimension.${d}`) }));

/** A quantity field's unit: one fixed unit, or (none fixed) the units allowed (none listed: any). */
function QuantitySettings({ r, set }: SettingProps) {
  const id = useId();
  const allowed = r.units ?? [];
  return (
    <>
      <div class="flex items-center gap-2">
        <label for={id} class={SETTING_LABEL}>
          {t("fields.unit")}
        </label>
        <Select
          id={id}
          compact={true}
          title={t("fields.unitHint")}
          value={r.unit ?? ""}
          onValue={(v) => set({ unit: v || null, ...(v ? { units: [] } : {}) })}
          options={[{ value: "", label: t("fields.anyUnit") }, ...unitOptions()]}
        />
      </div>
      {r.unit ? null : (
        <div class="flex flex-wrap items-center gap-1">
          <span class={SETTING_LABEL}>{t("fields.allowedUnits")}</span>
          {allowed.map((u) => (
            <Chip
              key={u}
              removeLabel={t("fields.removeUnit", { unit: unitSymbol(u) })}
              onRemove={() => set({ units: allowed.filter((x) => x !== u) })}
            >
              {unitSymbol(u)}
            </Chip>
          ))}
          <Select
            compact={true}
            aria-label={t("fields.addUnit")}
            value=""
            onValue={(v) => v && set({ units: [...allowed, v] })}
            options={[
              { value: "", label: allowed.length ? t("fields.addUnit") : t("fields.allUnits") },
              ...unitOptions().filter((o) => !allowed.includes(o.value)),
            ]}
          />
        </div>
      )}
    </>
  );
}

/** What the rows need besides their own fields. */
export interface Env {
  entities: readonly ObjEntity[];
  orgZone: string;
  /** Nothing can be changed (the toggles would be disabled too), so every field's settings show. */
  readOnly: boolean;
  space?: SpaceOut;
  /** The entity as saved (none while creating it): its items' values can become a field's options. */
  saved?: ObjEntity | null;
}

/** A row's size: link fields' rows are compact. */
export type Size = keyof typeof SQUARE;

/** A yes/no setting: a small checkbox with a short label, its full meaning in the tooltip. */
export function Flag(p: {
  text: string;
  title: string;
  label: string;
  checked: boolean;
  onCheck: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      class="inline-flex cursor-pointer items-center gap-1 text-xs text-zinc-600 has-disabled:cursor-not-allowed has-disabled:opacity-50"
      title={p.title}
    >
      <input
        type="checkbox"
        class={CHECKBOX}
        aria-label={p.label}
        checked={p.checked}
        disabled={p.disabled ?? false}
        onChange={(e) => p.onCheck(e.currentTarget.checked)}
      />
      {p.text}
    </label>
  );
}

/**
 * The settings only some types have: options, link target and uniqueness, currency, default zone, a text's format, a
 * phone's region, a quantity's units; uniqueness of values.
 */
export function Settings(p: SettingProps) {
  switch (p.r.type) {
    case "select":
      return <OptionsSetting {...p} />;
    case "ref":
      return <RefSettings {...p} />;
    case "money":
      return <MoneySettings {...p} />;
    case "datetime":
      return <ZoneSettings {...p} />;
    case "text":
      return <FormatSettings {...p} />;
    case "phone":
      return <PhoneSettings {...p} />;
    case "quantity":
      return <QuantitySettings {...p} />;
    case "location":
      return <NormalizeSetting {...p} />;
    default:
      return (
        <>
          <UniqueSetting {...p} />
          <NormalizeSetting {...p} />
        </>
      );
  }
}

/** Beside the type: the button showing or hiding the field's settings, or (no settings) a gap of its size. */
export function SettingsToggle(p: {
  size: Size;
  has: boolean;
  open: boolean;
  controls: string;
  label: string;
  onToggle: () => void;
}) {
  if (!p.has) {
    return <span class={`shrink-0 ${SQUARE[p.size]}`} />;
  }
  return (
    <IconButton
      size={p.size === "compact" ? "xs" : "md"}
      aria-expanded={p.open}
      aria-controls={p.controls}
      label={p.label}
      onClick={p.onToggle}
    >
      <SlidersIcon />
    </IconButton>
  );
}
